import { buildMeetingPath } from '../../utils/url';
import { applyDeviceLabelsToHostSettings } from './deviceBridge';
import { resolveMeetOrigin } from './meetOrigin';
import {
  isMeetingWindowMessage,
  MEETING_WINDOW_MESSAGE_SOURCE,
  MEETING_WINDOW_PARENT_ORIGIN_PARAM,
  MEETING_WINDOW_QUERY_FLAG,
  type MeetingWindowAuthSnapshot,
} from './protocol';
import { getHostSettingsPayload } from './settingsBridge';

/**
 * Opener-side controller for the hosted-mode meeting popup.
 *
 * Enforces a single meeting window at a time, hands the host identity to the
 * popup over `postMessage`, and watches for the window closing so callers can
 * clear their state. See `protocol.ts` for the wire contract and the design
 * rationale (Jitsi `external_api.js` isolation in MF mode).
 */

/** Supplies the current host identity on demand (always re-asked, never cached). */
export type MeetingWindowAuthProvider = () => Promise<MeetingWindowAuthSnapshot>;

export interface MeetingWindowHandle {
  /** Point the reserved (blank) window at the resolved meeting URL. */
  navigate: (meetingId: string, password?: string | null) => void;
  /** Close the reserved window and tear down listeners (e.g. on create failure). */
  cancel: () => void;
}

/**
 * The discriminants a reservation can resolve to. Kept as a `const` tuple so the
 * union below is derived from a single source of truth and callers can match it
 * exhaustively (see `assertUnreachable`).
 */
export const RESERVE_MEETING_WINDOW_STATUSES = ['reserved', 'existing', 'blocked'] as const;

export type ReserveMeetingWindowStatus = (typeof RESERVE_MEETING_WINDOW_STATUSES)[number];

export type ReserveMeetingWindowResult =
  | { status: Extract<ReserveMeetingWindowStatus, 'reserved'>; handle: MeetingWindowHandle }
  /** A meeting window is already open; it has been focused — caller should abort. */
  | { status: Extract<ReserveMeetingWindowStatus, 'existing'> }
  /** The browser blocked the popup; caller should prompt the user for a fallback. */
  | { status: Extract<ReserveMeetingWindowStatus, 'blocked'> };

export interface ReserveMeetingWindowParams {
  getAuth: MeetingWindowAuthProvider;
  /** Invoked once when the meeting window closes (manually or via teardown). */
  onClosed?: () => void;
}

const WINDOW_NAME = 'ngmeet-meeting-window';
const POPUP_WIDTH = 1280;
const POPUP_HEIGHT = 800;
const CLOSE_POLL_INTERVAL_MS = 500;
/**
 * How long to wait for the meeting window to answer a `switch-request`. Only
 * reached when the popup cannot prompt — e.g. an opener newer than the deployed
 * Meet page it opened — in which case we drop the switch and leave the user in
 * the meeting they are in, which is the pre-switch behaviour.
 */
const SWITCH_RESPONSE_TIMEOUT_MS = 60000;

interface ActiveMeetingWindow {
  win: Window;
  cleanup: () => void;
  /** The room the window was last pointed at; `null` until it is navigated. */
  roomId: string | null;
}

/** A switch the meeting window has been asked about but has not answered yet. */
interface PendingSwitch {
  requestId: string;
  meetingId: string;
  password?: string | null;
  timeoutId: number;
}

let active: ActiveMeetingWindow | null = null;
let pendingSwitch: PendingSwitch | null = null;

const clearPendingSwitch = (): void => {
  if (!pendingSwitch) {
    return;
  }
  window.clearTimeout(pendingSwitch.timeoutId);
  pendingSwitch = null;
};

const buildPopupUrl = (meetingId: string, password?: string | null): string => {
  const origin = resolveMeetOrigin();
  // `buildMeetingPath` yields `/room` or `/room#passcode`.
  const [pathname, hash] = buildMeetingPath(meetingId, password).split('#');
  const url = new URL(pathname, origin);
  url.searchParams.set(MEETING_WINDOW_QUERY_FLAG, '1');
  url.searchParams.set(MEETING_WINDOW_PARENT_ORIGIN_PARAM, window.location.origin);
  return `${url.toString()}${hash ? `#${hash}` : ''}`;
};

/**
 * Room ids are compared case-insensitively because Jitsi itself treats room
 * names that way, so `/Standup` and `/standup` are the same meeting and must
 * not look like a switch.
 */
const normalizeRoomId = (meetingId: string): string => meetingId.trim().toLowerCase();

/** Point the open window at a room, keeping the tracked room id in step. */
const navigateActiveWindow = (meetingId: string, password?: string | null): void => {
  if (!active || active.win.closed) {
    return;
  }
  active.roomId = meetingId;
  active.win.location.replace(buildPopupUrl(meetingId, password));
};

/**
 * Act on the meeting window's answer to a `switch-request`.
 *
 * Navigating from here — the opener — is what makes this a room switch rather
 * than a hangup: the desktop shell reads the handoff as a new meeting and
 * resets its room tracking, whereas a popup navigating itself looks like the
 * user leaving and gets the window closed.
 */
const resolveSwitchResponse = (requestId: string, accepted: boolean): void => {
  const pending = pendingSwitch;
  // Ignore an answer to a prompt that was superseded, timed out, or replayed.
  if (!pending || pending.requestId !== requestId) {
    return;
  }
  clearPendingSwitch();
  if (!accepted) {
    return;
  }
  navigateActiveWindow(pending.meetingId, pending.password);
};

const buildWindowFeatures = (): string => {
  const left = window.screenX + Math.max(0, Math.round((window.outerWidth - POPUP_WIDTH) / 2));
  const top = window.screenY + Math.max(0, Math.round((window.outerHeight - POPUP_HEIGHT) / 2));
  return `popup=yes,width=${POPUP_WIDTH},height=${POPUP_HEIGHT},left=${left},top=${top}`;
};

/**
 * Synchronously open (or focus) the meeting popup. MUST be called inside the
 * user gesture (click handler) before any `await`, otherwise the browser will
 * block the popup. The returned handle's `navigate()` can be called later,
 * after async work (e.g. creating the meeting) resolves the room id.
 */
export const reserveMeetingWindow = ({
  getAuth,
  onClosed,
}: ReserveMeetingWindowParams): ReserveMeetingWindowResult => {
  // Single instance: focus the existing window and block a second meeting.
  // If focus reveals the window is already gone (desktop stub after a missed
  // close signal), fall through and reserve a fresh one.
  if (active && !active.win.closed) {
    active.win.focus();
    if (!active.win.closed) {
      return { status: 'existing' };
    }
    active.cleanup();
  }
  if (active) {
    active.cleanup();
  }

  const meetOrigin = resolveMeetOrigin();
  const win = window.open('about:blank', WINDOW_NAME, buildWindowFeatures());
  if (!win) {
    return { status: 'blocked' };
  }

  const sendAuth = async (requestId: string | null): Promise<void> => {
    try {
      const auth = await getAuth();
      if (active?.win === win && !win.closed) {
        win.postMessage(
          { source: MEETING_WINDOW_MESSAGE_SOURCE, type: 'auth', requestId, auth },
          meetOrigin,
        );
      }
    } catch (err) {
      console.warn('[meetingWindow] failed to provide auth to popup', err);
    }
  };

  // Hand the host's Meet Settings to the popup. The popup builds its Jitsi
  // config from its own origin's storage, which is empty in hosted mode, so
  // without this every toggle falls back to defaults (SM-1187/1188/1190).
  const sendSettings = async (): Promise<void> => {
    try {
      const settings = await getHostSettingsPayload();
      if (active?.win === win && !win.closed) {
        win.postMessage(
          { source: MEETING_WINDOW_MESSAGE_SOURCE, type: 'settings', settings },
          meetOrigin,
        );
      }
    } catch (err) {
      console.warn('[meetingWindow] failed to provide settings to popup', err);
    }
  };

  const onMessage = (event: MessageEvent): void => {
    if (event.source !== win || event.origin !== meetOrigin) {
      return;
    }
    if (!isMeetingWindowMessage(event.data)) {
      return;
    }
    if (event.data.type === 'ready') {
      void sendAuth(null);
      void sendSettings();
    } else if (event.data.type === 'request-auth') {
      void sendAuth(event.data.requestId);
    } else if (event.data.type === 'device-update') {
      // Popup handed back its final in-meeting selection; reflect it in the
      // host Meet Settings (matched by label to host-origin device ids).
      void applyDeviceLabelsToHostSettings(event.data.devices);
    } else if (event.data.type === 'switch-response') {
      resolveSwitchResponse(event.data.requestId, event.data.accepted);
    }
  };

  let pollId = 0;
  const cleanup = (): void => {
    window.removeEventListener('message', onMessage);
    if (pollId) {
      window.clearInterval(pollId);
      pollId = 0;
    }
    if (active?.win === win) {
      active = null;
      // A prompt outlives neither the window it was shown in nor the meeting it
      // was about; a later window must not inherit it.
      clearPendingSwitch();
    }
    onClosed?.();
  };

  window.addEventListener('message', onMessage);
  pollId = window.setInterval(() => {
    if (win.closed) {
      cleanup();
    }
  }, CLOSE_POLL_INTERVAL_MS);

  active = { win, cleanup, roomId: null };
  win.focus();

  return {
    status: 'reserved',
    handle: {
      navigate: (meetingId, password) => {
        if (win.closed) {
          return;
        }
        if (active?.win === win) {
          active.roomId = meetingId;
        }
        win.location.replace(buildPopupUrl(meetingId, password));
      },
      cancel: () => {
        try {
          win.close();
        } finally {
          cleanup();
        }
      },
    },
  };
};

/**
 * Focus the tracked meeting popup if one is open, without opening or reserving
 * a new one. Used by launch sites (e.g. the hosted Join tile) that should defer
 * to an already-open meeting instead of prompting for a new one. Cleans up
 * stale state if the tracked window was closed without going through
 * `cleanup()` (e.g. closed while this tab was backgrounded).
 */
export const focusMeetingWindow = (): boolean => {
  if (!active) {
    return false;
  }
  if (active.win.closed) {
    active.cleanup();
    return false;
  }
  active.win.focus();
  return true;
};

/**
 * True when the open meeting window is already on `meetingId`.
 *
 * Lets launch sites tell "clicked the meeting that is already running" — where
 * focusing the window is the whole job — from "clicked a different meeting",
 * which has to ask before replacing it. False when no window is open, or when
 * one was reserved but never navigated (so its room is not known yet).
 */
export const isOpenMeetingRoom = (meetingId: string): boolean => {
  if (!active || active.win.closed || active.roomId === null) {
    return false;
  }
  return normalizeRoomId(active.roomId) === normalizeRoomId(meetingId);
};

/**
 * Ask the open meeting window whether to leave its meeting and join
 * `meetingId`, and switch it over if the user accepts.
 *
 * The prompt is rendered by the popup (see `MeetingSwitchProvider`) because that
 * is where the meeting being left lives, and because the opener may have no Meet
 * UI mounted — the link can come from Chat via the inter-module API (SM-1254).
 *
 * Returns `false` when there is no window to ask, so the caller can fall back to
 * a normal launch. `true` means Meet owns the outcome from here: the switch
 * happens, or the user declines and stays where they are.
 */
export const requestMeetingSwitch = ({
  meetingId,
  password,
}: {
  meetingId: string;
  password?: string | null;
}): boolean => {
  if (!active || active.win.closed) {
    return false;
  }

  // One prompt at a time: a second link supersedes the first rather than
  // stacking dialogs, so the newest thing the user clicked is what they get.
  clearPendingSwitch();

  const requestId = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  const timeoutId = window.setTimeout(() => {
    if (pendingSwitch?.requestId === requestId) {
      pendingSwitch = null;
    }
  }, SWITCH_RESPONSE_TIMEOUT_MS);
  pendingSwitch = { requestId, meetingId, password, timeoutId };

  // Focus first: the prompt is in that window, so it has to be visible.
  active.win.focus();
  active.win.postMessage(
    { source: MEETING_WINDOW_MESSAGE_SOURCE, type: 'switch-request', requestId, meetingId },
    resolveMeetOrigin(),
  );
  return true;
};

/**
 * Convenience for the synchronous launch path (meeting id already known, e.g.
 * "Join by ID"). Reserves the window and immediately navigates it.
 */
export const openMeetingWindow = (params: {
  meetingId: string;
  password?: string | null;
  getAuth: MeetingWindowAuthProvider;
  onClosed?: () => void;
}): ReserveMeetingWindowResult => {
  const result = reserveMeetingWindow({ getAuth: params.getAuth, onClosed: params.onClosed });
  if (result.status === 'reserved') {
    result.handle.navigate(params.meetingId, params.password);
  }
  return result;
};
